// src/pages/admin/AdminHanmotoPage.tsx
// 版元ドットコム（hanmoto_books）取り込みバッチの状況画面。
// NDLとは別系統のバッチ（`hanmoto_crawl_worker`、EventBridge週次cron）のため、
// NDLダッシュボードとは別画面として独立させている（ユーザー確認済み）。

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useAuth } from '../../context/AuthContext';
import { useAppTokens } from '../../context/ThemeModeContext';
import { NEW_API } from '../../config/api';
import AdminNav from './AdminNav';

interface BatchStat {
  total_count: number;
  last_run_delta: number;
  last_run_at: string | null;
  updated_at: string | null;
}

interface DailyStatRow {
  run_date: string;
  new_count: number;
  run_count: number;
  last_run_at: string | null;
}

const AdminHanmotoPage: React.FC = () => {
  const TOKENS = useAppTokens();
  const { idToken } = useAuth();

  const [stat, setStat] = useState<BatchStat | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [dailyRows, setDailyRows] = useState<DailyStatRow[] | null>(null);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [dailyError, setDailyError] = useState<string | null>(null);

  const cardSx = {
    border: `1px solid ${TOKENS.border}`,
    background: TOKENS.glass,
    borderRadius: '18px',
    p: 2.5,
  } as const;

  const fetchStat = useCallback(async () => {
    if (!idToken) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(
        NEW_API.ADMIN,
        { operation_type: 'get_batch_stats' },
        { headers: { Authorization: `Bearer ${idToken}` } },
      );
      setStat(res.data?.data?.hanmoto_books ?? null);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || '統計の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [idToken]);

  const fetchDailyStats = useCallback(async () => {
    if (!idToken) return;
    setDailyLoading(true);
    setDailyError(null);
    try {
      const res = await axios.post(
        NEW_API.ADMIN,
        { operation_type: 'get_hanmoto_daily_stats', days: 30 },
        { headers: { Authorization: `Bearer ${idToken}` } },
      );
      setDailyRows(res.data?.data ?? []);
    } catch (e: any) {
      setDailyError(e?.response?.data?.error?.message || '実行日ごとの結果の取得に失敗しました');
    } finally {
      setDailyLoading(false);
    }
  }, [idToken]);

  useEffect(() => {
    fetchStat();
    fetchDailyStats();
  }, [fetchStat, fetchDailyStats]);

  const formatDateTime = (iso: string | null) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('ja-JP');
    } catch {
      return iso;
    }
  };

  return (
    <Box sx={{ minHeight: '100dvh', background: TOKENS.bg, color: TOKENS.text }}>
      <AdminNav />
      <Box sx={{ maxWidth: 900, margin: 'auto', px: 2, py: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 700 }}>版元ドットコム取り込み</Typography>
          <Button
            onClick={() => {
              fetchStat();
              fetchDailyStats();
            }}
            disabled={loading || dailyLoading}
            startIcon={loading || dailyLoading ? <CircularProgress size={14} sx={{ color: TOKENS.textSub }} /> : <RefreshIcon />}
            sx={{ textTransform: 'none', color: TOKENS.textSub }}
          >
            更新
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography sx={{ fontSize: 13, color: TOKENS.textSub, mb: 2 }}>
          版元ドットコムからの近刊・書誌データ取り込みバッチ（`hanmoto_crawl_worker`）の状況です。NDLの取り込みとは別系統です。
        </Typography>

        <Box sx={{ ...cardSx, maxWidth: 320, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, color: TOKENS.textSub }}>
            <StorefrontIcon />
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>hanmoto_books（累計冊数）</Typography>
          </Box>
          {loading && !stat ? (
            <CircularProgress size={20} sx={{ color: TOKENS.text }} />
          ) : (
            <>
              <Typography sx={{ fontSize: 32, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {stat ? stat.total_count.toLocaleString('ja-JP') : '—'}
              </Typography>
              <Typography sx={{ fontSize: 12, color: TOKENS.textSub, mt: 0.5 }}>
                前回実行分: {stat && stat.last_run_delta > 0 ? `+${stat.last_run_delta.toLocaleString('ja-JP')}` : '±0'}
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: TOKENS.textSub }}>
                最終更新: {formatDateTime(stat?.last_run_at ?? null)}
              </Typography>
            </>
          )}
        </Box>

        <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 1 }}>実行日ごとの結果</Typography>
        <Typography sx={{ fontSize: 12, color: TOKENS.textSub, mb: 2 }}>
          バッチが実際に実行された日だけが表示されます（過去分は遡って記録していません）。
        </Typography>

        {dailyError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {dailyError}
          </Alert>
        )}

        {dailyLoading && !dailyRows ? (
          <CircularProgress size={20} sx={{ color: TOKENS.text }} />
        ) : !dailyRows || dailyRows.length === 0 ? (
          <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub }}>
            まだ記録がありません。次にバッチが実行されるとここに表示されます。
          </Typography>
        ) : (
          <TableContainer sx={{ ...cardSx, p: 0 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: TOKENS.textSub, fontSize: 12, borderColor: TOKENS.border }}>日付</TableCell>
                  <TableCell sx={{ color: TOKENS.textSub, fontSize: 12, borderColor: TOKENS.border }} align="right">
                    新規冊数
                  </TableCell>
                  <TableCell sx={{ color: TOKENS.textSub, fontSize: 12, borderColor: TOKENS.border }} align="right">
                    実行回数
                  </TableCell>
                  <TableCell sx={{ color: TOKENS.textSub, fontSize: 12, borderColor: TOKENS.border }}>
                    最終実行時刻
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dailyRows.map((row) => (
                  <TableRow key={row.run_date}>
                    <TableCell sx={{ color: TOKENS.text, fontSize: 12.5, borderColor: TOKENS.border }}>
                      {row.run_date}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: TOKENS.text, fontSize: 12.5, borderColor: TOKENS.border, fontVariantNumeric: 'tabular-nums' }}
                    >
                      +{row.new_count.toLocaleString('ja-JP')}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: TOKENS.textSub, fontSize: 12.5, borderColor: TOKENS.border, fontVariantNumeric: 'tabular-nums' }}
                    >
                      {row.run_count.toLocaleString('ja-JP')}
                    </TableCell>
                    <TableCell sx={{ color: TOKENS.textSub, fontSize: 11.5, borderColor: TOKENS.border }}>
                      {formatDateTime(row.last_run_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};

export default AdminHanmotoPage;
